import { minDate, maxDate, generateHourlyDatetimes, hourDiff } from './time';
import {
  celsiusToFahrenheit,
  metersToFeet,
  mmToInches,
  kmphToMph,
} from './unitConversion';
import { decompressData, interpolateData, cumulativeSum } from './timeseries';

function trim(arr, startOffset, endOffset) {
  return arr.slice(startOffset, arr.length - endOffset);
}

export function preprocessForecast(forecast) {
  const quantities = [
    {
      quantity: 'snowLevel',
      processor: decompressData,
      unitConverter: metersToFeet,
    },
    {
      quantity: 'snowfallAmount',
      processor: interpolateData,
      unitConverter: mmToInches,
    },
    {
      quantity: 'probabilityOfPrecipitation',
      processor: decompressData,
    },
    {
      quantity: 'quantitativePrecipitation',
      processor: interpolateData,
      unitConverter: mmToInches,
    },
    {
      quantity: 'windSpeed',
      processor: decompressData,
      unitConverter: kmphToMph,
    },
    {
      quantity: 'windGust',
      processor: decompressData,
      unitConverter: kmphToMph,
    },
    {
      quantity: 'windDirection',
      processor: decompressData,
    },
    {
      quantity: 'temperature',
      processor: decompressData,
      unitConverter: celsiusToFahrenheit,
    },
    {
      quantity: 'skyCover',
      processor: decompressData,
    },
    {
      quantity: 'windDirection',
      processor: decompressData,
    },
  ];

  const values = quantities.map((quantity) => {
    const data = quantity.processor(forecast[quantity.quantity].values);
    const times = data.map((datum) => datum['validTime']);

    return {
      quantity: quantity.quantity,
      values: data.map((datum) =>
        quantity.unitConverter
          ? quantity.unitConverter(datum['value'])
          : datum['value']
      ),
      startTime: minDate(times),
      endTime: maxDate(times),
    };
  });

  const startTime = maxDate(values.map((value) => value.startTime));
  const endTime = minDate(values.map((value) => value.endTime));

  const timeAlignedValues = values.map((value) => {
    const startOffset = hourDiff(value.startTime, startTime);
    const endOffset = hourDiff(endTime, value.endTime);

    return {
      quantity: value.quantity,
      values: trim(value.values, startOffset, endOffset),
    };
  });

  let forecastData = timeAlignedValues.reduce((accumulator, currentItem) => {
    accumulator[currentItem.quantity] = currentItem.values;
    return accumulator;
  }, {});

  forecastData['cumulativeSnowfallAmount'] = cumulativeSum(
    forecastData.snowfallAmount.map(
      (snowfallAmount, index) =>
        (snowfallAmount * forecastData.probabilityOfPrecipitation[index]) / 100
    )
  );

  forecastData['precipitation'] = forecastData.quantitativePrecipitation.map(
    (precip, index) =>
      (precip * forecastData.probabilityOfPrecipitation[index]) / 100
  );

  return {
    elevation: metersToFeet(forecast['elevation']['value']),
    times: generateHourlyDatetimes(startTime, endTime),
    data: forecastData,
  };
}

function alignForecast(forecast, startTime, endTime) {
  const startOffset = hourDiff(forecast.times[0], startTime);
  const endOffset = hourDiff(
    endTime,
    forecast.times[forecast.times.length - 1]
  );

  const times = trim(forecast.times, startOffset, endOffset);
  const data = Object.entries(forecast.data).map((key, values) =>
    trim(values, startOffset, endOffset)
  );

  return {
    ...forecast,
    times: times,
    data: data,
  };
}

export function alignForecasts(forecasts) {
  const startTime = maxDate(forecasts.map((forecast) => forecast.times[0]));
  const endTime = minDate(
    forecasts.map((forecast) => forecast.times[forecast.times.length - 1])
  );

  return forecasts.map((forecast) =>
    alignForecast(forecast, startTime, endTime)
  );
}
